/**
 * User Rank Banner
 * Displays the current user's rank on the leaderboard
 */

import type { UserRank } from '@parlay-picker/shared/hooks';
import type { ReactElement } from 'react';

// =============================================================================
// Types
// =============================================================================

interface UserRankBannerProps {
  userRank: UserRank;
}

// =============================================================================
// Component
// =============================================================================

export function UserRankBanner({ userRank }: UserRankBannerProps): ReactElement {
  if (!userRank.rank) {
    return (
      <div className="bg-theme-surface/50 border border-theme-border rounded-lg p-4 text-center">
        <p className="text-theme-text-tertiary">
          You haven't qualified for the leaderboard yet. Track more bets to see your rank!
        </p>
      </div>
    );
  }

  const percentileColor =
    userRank.percentile && userRank.percentile >= 90
      ? 'text-theme-success'
      : userRank.percentile && userRank.percentile >= 70
        ? 'text-theme-primary'
        : userRank.percentile && userRank.percentile >= 50
          ? 'text-theme-warning'
          : 'text-theme-text-tertiary';

  return (
    <div
      data-testid="user-rank-banner"
      className="bg-gradient-to-r from-theme-primary/10 to-purple-500/10 border border-theme-primary/20 rounded-lg p-6"
    >
      <div className="flex items-center justify-center space-x-8">
        <div className="text-center">
          <div className="text-4xl font-bold text-theme-text">#{userRank.rank}</div>
          <div className="text-sm text-theme-text-tertiary mt-1">Your Rank</div>
        </div>
        {userRank.percentile !== null && (
          <>
            <div className="h-12 w-px bg-theme-border" />
            <div className="text-center">
              <div className={`text-4xl font-bold ${percentileColor}`}>
                Top {(100 - userRank.percentile).toFixed(0)}%
              </div>
              <div className="text-sm text-theme-text-tertiary mt-1">Percentile</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
